import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  deleteField,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { setDoc, updateDoc, deleteDoc, confirmarLote } from '../config/escrituras';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { usePerfil } from './PerfilContext';
import { comprobarLimite } from '../config/planes';
import { cuentasNormales, huchas, inversionesActivas, limiteDeCuenta } from '../utils/limites';
import { Cuenta, Categoria, Transaccion, Presupuesto, Traspaso, TraspasoRecurrente, TransaccionRecurrente, Inversion, AportacionInversion, RetiroInversion, ValoracionInversion } from '../types';
import { calcularComisionAportacion } from '../utils/inversionUtils';
import { esAdelantoDe, CATEGORIA_ADELANTO } from '../utils/transaccionUtils';
import { DiferenciasSincronizacion } from '../utils/sincronizacionAMedias';
import { alApuntar } from '../utils/recordatoriosGastos';
import { proximaTrasEjecutar } from '../utils/periodicos';

interface DataContextType {
  cuentas: Cuenta[];
  categorias: Categoria[];
  categoriasCargadas: boolean;
  /** Ya llegó la primera lectura de cuentas. Antes, la lista vacía no significa «no hay». */
  cuentasCargadas: boolean;
  /** Ya llegó la primera lectura de transacciones. Antes, la lista vacía no significa «no hay». */
  transaccionesCargadas: boolean;
  transacciones: Transaccion[];
  presupuestos: Presupuesto[];
  traspasos: Traspaso[];
  traspasosRecurrentes: TraspasoRecurrente[];
  inversiones: Inversion[];
  aportacionesInversion: AportacionInversion[];
  retirosInversion: RetiroInversion[];
  valoracionesInversion: ValoracionInversion[];
  loading: boolean;

  // Cuentas
  crearCuenta: (cuenta: Omit<Cuenta, 'id' | 'usuarioId' | 'createdAt'>) => Promise<void>;
  actualizarCuenta: (id: string, cuenta: Partial<Cuenta>) => Promise<void>;
  eliminarCuenta: (id: string) => Promise<void>;

  // Categorías
  crearCategoria: (categoria: Omit<Categoria, 'id' | 'usuarioId'>) => Promise<void>;
  crearCategorias: (categorias: Omit<Categoria, 'id' | 'usuarioId'>[]) => Promise<void>;
  actualizarCategoria: (id: string, categoria: Partial<Categoria>) => Promise<void>;
  eliminarCategoria: (id: string) => Promise<void>;

  // Transacciones
  crearTransaccion: (transaccion: Omit<Transaccion, 'id' | 'usuarioId' | 'createdAt'>) => Promise<void>;
  actualizarTransaccion: (id: string, datosNuevos: Partial<Transaccion>, datosOriginales: Transaccion) => Promise<void>;
  eliminarTransaccion: (id: string) => Promise<void>;
  /** Da por cerradas las cuentas de adelantos con una persona, quede el resto que quede. */
  saldarAdelantosDe: (persona: string) => Promise<void>;
  /** Aplica de una vez lo que pide la sincronización con un plan de A medias. */
  aplicarSincronizacionPlan: (
    planId: string, cambios: DiferenciasSincronizacion, destino: { cuentaId: string; categoriaId: string },
  ) => Promise<void>;

  // Presupuestos
  crearPresupuesto: (presupuesto: Omit<Presupuesto, 'id' | 'usuarioId' | 'createdAt'>) => Promise<void>;
  actualizarPresupuesto: (id: string, presupuesto: Partial<Presupuesto>) => Promise<void>;
  eliminarPresupuesto: (id: string) => Promise<void>;
  migrarPresupuestoAAnual: (params: {
    presupuestoMensualId: string;
    anioInicio: number;
    mesInicio: number;
  }) => Promise<string>; // devuelve el id del nuevo presupuesto anual

  // Traspasos
  crearTraspaso: (traspaso: Omit<Traspaso, 'id' | 'usuarioId' | 'createdAt'>) => Promise<void>;
  eliminarTraspaso: (id: string) => Promise<void>;

  // Traspasos recurrentes
  crearTraspasoRecurrente: (tr: Omit<TraspasoRecurrente, 'id' | 'usuarioId' | 'createdAt'>) => Promise<void>;
  actualizarTraspasoRecurrente: (id: string, tr: Partial<TraspasoRecurrente>) => Promise<void>;
  ejecutarTraspasoRecurrente: (id: string) => Promise<void>;
  eliminarTraspasoRecurrente: (id: string) => Promise<void>;

  // Inversiones
  crearInversion: (inv: Omit<Inversion, 'id' | 'usuarioId' | 'createdAt'>) => Promise<void>;
  actualizarInversion: (id: string, inv: Partial<Inversion>) => Promise<void>;
  eliminarInversion: (id: string) => Promise<void>;
  actualizarValorInversion: (id: string, valor: number, fecha: Date) => Promise<void>;
  eliminarAportacionInversion: (id: string) => Promise<void>;
  crearTraspasoAInversion: (params: {
    cuentaOrigenId: string;
    inversionDestinoId: string;
    cantidad: number;
    descripcion?: string;
    fecha: Date;
    presupuestoDestinoId?: string;
  }) => Promise<void>;
  crearRetiroDesdeInversion: (params: {
    inversionId: string;
    cuentaDestinoId: string;
    cantidad: number;
    descripcion?: string;
    fecha: Date;
  }) => Promise<void>;
  eliminarRetiroInversion: (id: string) => Promise<void>;

  // Transacciones recurrentes
  transaccionesRecurrentes: TransaccionRecurrente[];
  crearTransaccionRecurrente: (tr: Omit<TransaccionRecurrente, 'id' | 'usuarioId' | 'createdAt'>) => Promise<void>;
  actualizarTransaccionRecurrente: (id: string, tr: Partial<TransaccionRecurrente>) => Promise<void>;
  ejecutarTransaccionRecurrente: (id: string) => Promise<void>;
  eliminarTransaccionRecurrente: (id: string) => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  // Solo se escucha con el módulo activado: sin él, las reglas rechazan la
  // consulta y el listener muere; al activarlo, el efecto vuelve a suscribirse.
  const { tieneModulo, esPlus } = usePerfil();
  const conModulo = tieneModulo('finanzas');
  // Los límites del plan gratis se comprueban también aquí, antes de guardar,
  // por si algún botón se salta useLimite(). Las reglas no pueden contar.

  // Estado raw de Firestore — saldo NO se almacena; se calcula
  const [cuentasRaw, setCuentasRaw] = useState<Cuenta[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  // `loading` se apaga antes de que lleguen los datos; esto sí dice si las
  // categorías ya se han leído (para no confundir «cargando» con «no tiene»)
  const [categoriasCargadas, setCategoriasCargadas] = useState(false);
  const [cuentasCargadas, setCuentasCargadas] = useState(false);
  const [transaccionesCargadas, setTransaccionesCargadas] = useState(false);
  const [transacciones, setTransacciones] = useState<Transaccion[]>([]);
  const [presupuestos, setPresupuestos] = useState<Presupuesto[]>([]);
  const [traspasos, setTraspasos] = useState<Traspaso[]>([]);
  const [traspasosRecurrentes, setTraspasosRecurrentes] = useState<TraspasoRecurrente[]>([]);
  const [transaccionesRecurrentes, setTransaccionesRecurrentes] = useState<TransaccionRecurrente[]>([]);
  const [inversiones, setInversiones] = useState<Inversion[]>([]);
  const [aportacionesInversion, setAportacionesInversion] = useState<AportacionInversion[]>([]);
  const [retirosInversion, setRetirosInversion] = useState<RetiroInversion[]>([]);
  const [valoracionesInversion, setValoracionesInversion] = useState<ValoracionInversion[]>([]);
  const [loading, setLoading] = useState(true);

  // ── Saldo calculado: saldoInicial + Σingresos − Σgastos ─────────────────
  const cuentas = useMemo<Cuenta[]>(() =>
    cuentasRaw.map(cuenta => {
      const ingresos = transacciones
        .filter(t => t.cuentaId === cuenta.id && t.tipo === 'ingreso')
        .reduce((s, t) => s + t.cantidad, 0);
      const gastos = transacciones
        .filter(t => t.cuentaId === cuenta.id && t.tipo === 'gasto')
        .reduce((s, t) => s + t.cantidad, 0);
      return { ...cuenta, saldo: (cuenta.saldoInicial ?? 0) + ingresos - gastos };
    }),
  [cuentasRaw, transacciones]);

  // ── Listeners Firestore ──────────────────────────────────────────────────
  useEffect(() => {
    if (!user || !conModulo) {
      setCuentasRaw([]);
      setCategorias([]);
      setTransacciones([]);
      setPresupuestos([]);
      setTraspasos([]);
      setTraspasosRecurrentes([]);
      setTransaccionesRecurrentes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setCategoriasCargadas(false);
    setCuentasCargadas(false);
    setTransaccionesCargadas(false);

    const q = (col: string) => query(collection(db, col), where('usuarioId', '==', user.uid));

    const unsubCuentas = onSnapshot(q('cuentas'), snap => {
      setCuentasRaw(snap.docs.map(d => ({
        ...d.data(),
        id: d.id,
        createdAt: d.data().createdAt?.toDate() || new Date(),
        saldoInicial: d.data().saldoInicial ?? d.data().saldo ?? 0,
        saldo: 0, // se sobreescribe por useMemo; el valor aquí no importa
      })) as Cuenta[]);
      setCuentasCargadas(true);
    });

    const unsubCategorias = onSnapshot(q('categorias'), snap => {
      setCategorias(snap.docs.map(d => ({ ...d.data(), id: d.id })) as Categoria[]);
      setCategoriasCargadas(true);
    });

    const unsubTransacciones = onSnapshot(q('transacciones'), snap => {
      setTransacciones(snap.docs.map(d => {
        const data = d.data();
        // IMPORTANTE: no usar toMillis() como fallback — generaría números de 13 dígitos
        // que envenenarían nextSeq() y producirían saltos enormes en las secuencias.
        const secAlmacenada = data.secuencia;
        const secLimpia =
          typeof secAlmacenada === 'number' && secAlmacenada < 1_000_000_000
            ? secAlmacenada   // número secuencial normal (< 1 000 millones)
            : 0;              // ignorar timestamps-ms o campos ausentes
        return {
          ...data,
          id: d.id,
          createdAt: data.createdAt?.toDate() || new Date(),
          fecha: data.fecha?.toDate() || new Date(),
          secuencia: secLimpia,
        };
      }) as Transaccion[]);
      setTransaccionesCargadas(true);
    });

    const unsubPresupuestos = onSnapshot(q('presupuestos'), snap => {
      setPresupuestos(snap.docs.map(d => ({
        ...d.data(),
        id: d.id,
        createdAt: d.data().createdAt?.toDate() || new Date(),
        periodicidad: d.data().periodicidad || 'mensual',
        repetitivo: d.data().repetitivo || false,
      })) as Presupuesto[]);
    });

    const unsubTraspasos = onSnapshot(q('traspasos'), snap => {
      setTraspasos(snap.docs.map(d => {
        const data = d.data();
        return {
          ...data,
          id: d.id,
          createdAt: data.createdAt?.toDate() || new Date(),
          fecha: data.fecha?.toDate() || new Date(),
          secuencia: data.secuencia ?? 0,
        };
      }) as Traspaso[]);
    });

    const unsubRecurrentes = onSnapshot(q('traspasosRecurrentes'), snap => {
      setTraspasosRecurrentes(snap.docs.map(d => {
        const data = d.data();
        return {
          ...data,
          id: d.id,
          createdAt: data.createdAt?.toDate() || new Date(),
          proximaFecha: data.proximaFecha?.toDate() || new Date(),
          ...(data.fechaFin ? { fechaFin: data.fechaFin.toDate() } : {}),
        };
      }) as TraspasoRecurrente[]);
    });

    const unsubTxRecurrentes = onSnapshot(q('transaccionesRecurrentes'), snap => {
      setTransaccionesRecurrentes(snap.docs.map(d => {
        const data = d.data();
        return {
          ...data,
          id: d.id,
          createdAt: data.createdAt?.toDate() || new Date(),
          proximaFecha: data.proximaFecha?.toDate() || new Date(),
          ...(data.fechaFin ? { fechaFin: data.fechaFin.toDate() } : {}),
        };
      }) as TransaccionRecurrente[]);
    });

    const unsubInversiones = onSnapshot(q('inversiones'), snap => {
      setInversiones(snap.docs.map(d => {
        const data = d.data();
        return {
          ...data, id: d.id,
          fechaInicio: data.fechaInicio?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
        };
      }) as Inversion[]);
    });

    const unsubAportaciones = onSnapshot(q('aportacionesInversion'), snap => {
      setAportacionesInversion(snap.docs.map(d => {
        const data = d.data();
        return {
          ...data, id: d.id,
          fecha: data.fecha?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
        };
      }) as AportacionInversion[]);
    });

    const unsubRetiros = onSnapshot(q('retirosInversion'), snap => {
      setRetirosInversion(snap.docs.map(d => {
        const data = d.data();
        return {
          ...data, id: d.id,
          fecha: data.fecha?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
        };
      }) as RetiroInversion[]);
    });

    const unsubValoraciones = onSnapshot(q('valoracionesInversion'), snap => {
      setValoracionesInversion(snap.docs.map(d => {
        const data = d.data();
        return {
          ...data, id: d.id,
          fecha: data.fecha?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
        };
      }) as ValoracionInversion[]);
    });

    setLoading(false);

    return () => {
      unsubCuentas(); unsubCategorias(); unsubTransacciones();
      unsubPresupuestos(); unsubTraspasos(); unsubRecurrentes();
      unsubTxRecurrentes(); unsubInversiones(); unsubAportaciones(); unsubRetiros(); unsubValoraciones();
    };
  }, [user, conModulo]);

  // ── Secuencia incremental ────────────────────────────────────────────────
  // Solo considera secuencias "limpias" (< 1 000 millones).
  // Valores ≥ 1_000_000_000 son timestamps-ms generados erróneamente por
  // el fallback antiguo de toMillis() o por Date.now() en la función Netlify.
  const nextSeq = () => {
    const seqs = transacciones
      .map(t => t.secuencia ?? 0)
      .filter(s => s > 0 && s < 1_000_000_000);
    return seqs.length === 0 ? 1 : Math.max(...seqs) + 1;
  };

  // ── CUENTAS ──────────────────────────────────────────────────────────────
  const crearCuenta = async (cuenta: Omit<Cuenta, 'id' | 'usuarioId' | 'createdAt'>) => {
    if (!user) throw new Error('Usuario no autenticado');
    // Spread de todos los campos (incluye icono, objetivo, etc.)
    // saldoInicial = saldo pasado en la creación; no guardamos "saldo" (es campo calculado)
    const clave = limiteDeCuenta(cuenta.tipo);
    comprobarLimite(clave, clave === 'huchas' ? huchas(cuentasRaw) : cuentasNormales(cuentasRaw), esPlus);
    const { saldo, ...resto } = cuenta;
    const datos = Object.fromEntries(Object.entries({ ...resto, saldoInicial: saldo }).filter(([, v]) => v !== undefined));
    await setDoc(doc(collection(db, 'cuentas')), {
      ...datos,
      usuarioId: user.uid,
      createdAt: Timestamp.now(),
    });
  };

  const actualizarCuenta = async (id: string, cuenta: Partial<Cuenta>) => {
    // Firestore rechaza campos con valor undefined — los eliminamos antes de enviar
    const datos = Object.fromEntries(
      Object.entries(cuenta).filter(([, v]) => v !== undefined)
    );
    await updateDoc(doc(db, 'cuentas', id), datos);
  };

  const eliminarCuenta = async (id: string) => {
    await deleteDoc(doc(db, 'cuentas', id));
  };

  // ── CATEGORÍAS ───────────────────────────────────────────────────────────
  const crearCategoria = async (categoria: Omit<Categoria, 'id' | 'usuarioId'>) => {
    if (!user) throw new Error('Usuario no autenticado');
    comprobarLimite('categorias', categorias.length, esPlus);
    await setDoc(doc(collection(db, 'categorias')), { ...categoria, usuarioId: user.uid });
  };

  // Varias de golpe (plantilla): o se crean todas o ninguna
  const crearCategorias = async (lista: Omit<Categoria, 'id' | 'usuarioId'>[]) => {
    if (!user) throw new Error('Usuario no autenticado');
    comprobarLimite('categorias', categorias.length, esPlus, lista.length);
    const batch = writeBatch(db);
    lista.forEach(c => batch.set(doc(collection(db, 'categorias')), { ...c, usuarioId: user.uid }));
    await confirmarLote(batch);
  };

  const actualizarCategoria = async (id: string, categoria: Partial<Categoria>) => {
    const datos = Object.fromEntries(Object.entries(categoria).filter(([, v]) => v !== undefined));
    await updateDoc(doc(db, 'categorias', id), datos);
  };

  const eliminarCategoria = async (id: string) => {
    await deleteDoc(doc(db, 'categorias', id));
  };

  // ── TRANSACCIONES ────────────────────────────────────────────────────────
  // El saldo de las cuentas se recalcula automáticamente via useMemo.
  // Ninguna operación de transacción actualiza el saldo en Firestore.

  const crearTransaccion = async (transaccion: Omit<Transaccion, 'id' | 'usuarioId' | 'createdAt'>) => {
    if (!user) throw new Error('Usuario no autenticado');
    const seq = transaccion.secuencia ?? nextSeq();
    await setDoc(doc(collection(db, 'transacciones')), {
      ...transaccion,
      usuarioId: user.uid,
      createdAt: Timestamp.now(),
      secuencia: seq,
      fecha: Timestamp.fromDate(new Date(transaccion.fecha)),
    });
    // Ya ha apuntado algo hoy: fuera el recordatorio diario de hoy
    alApuntar().catch(() => {});
  };

  const actualizarTransaccion = async (id: string, datosNuevos: Partial<Transaccion>, _datosOriginales: Transaccion) => {
    const updateData: any = { ...datosNuevos };
    if (datosNuevos.fecha) updateData.fecha = Timestamp.fromDate(new Date(datosNuevos.fecha));
    // Limpiar presupuestoId si se vacía
    if (datosNuevos.presupuestoId === undefined) delete updateData.presupuestoId;
    // Un adelanto no se imputa a ningún presupuesto: quitar el vínculo si lo tenía
    if (datosNuevos.adelanto) updateData.presupuestoId = deleteField();
    // Y si deja de ser adelanto, ya no tiene persona asociada ni puede estar saldado
    if (datosNuevos.adelanto === false) {
      updateData.persona = deleteField();
      updateData.saldado = deleteField();
    }
    await updateDoc(doc(db, 'transacciones', id), updateData);
  };

  const eliminarTransaccion = async (id: string) => {
    await deleteDoc(doc(db, 'transacciones', id));
  };

  // Marca como saldados los adelantos abiertos de la persona. No crea movimientos:
  // el saldo de las cuentas ya refleja lo que de verdad se pagó y se cobró, así
  // que el resto (unos céntimos de más o de menos) simplemente deja de contar.
  const saldarAdelantosDe = async (persona: string) => {
    const abiertos = transacciones.filter(t => esAdelantoDe(t, persona) && !t.saldado);
    // Un batch admite 500 escrituras como mucho
    for (let i = 0; i < abiertos.length; i += 450) {
      const batch = writeBatch(db);
      abiertos.slice(i, i + 450).forEach(t => batch.update(doc(db, 'transacciones', t.id), { saldado: true }));
      await confirmarLote(batch);
    }
  };

  // ── SINCRONIZACIÓN CON A MEDIAS ──────────────────────────────────────────
  // Todo en un batch: o queda el plan reflejado entero o no se toca nada.
  // La cuenta y la categoría solo se ponen al crear; al actualizar se respetan
  // las que el usuario haya cambiado a mano.
  const aplicarSincronizacionPlan: DataContextType['aplicarSincronizacionPlan'] = async (planId, cambios, destino) => {
    if (!user) throw new Error('Usuario no autenticado');
    const ops: ((b: ReturnType<typeof writeBatch>) => void)[] = [];
    let seq = nextSeq();

    cambios.crear.forEach(e => {
      const datos: Record<string, any> = {
        tipo: e.tipo,
        cantidad: e.cantidad,
        categoriaId: e.adelanto ? CATEGORIA_ADELANTO : destino.categoriaId,
        cuentaId: destino.cuentaId,
        descripcion: e.descripcion,
        fecha: Timestamp.fromDate(e.fecha),
        usuarioId: user.uid,
        createdAt: Timestamp.now(),
        secuencia: seq++,
        planId,
        origenCompartido: e.clave,
        compartidoCreadoPor: e.creadoPor,
      };
      if (e.adelanto) { datos.adelanto = true; datos.persona = e.persona; }
      ops.push(b => b.set(doc(collection(db, 'transacciones')), datos));
    });

    cambios.actualizar.forEach(({ transaccion: t, esperado: e }) => {
      const datos: Record<string, any> = {
        tipo: e.tipo,
        cantidad: e.cantidad,
        fecha: Timestamp.fromDate(e.fecha),
        descripcion: e.descripcion,
      };
      if (e.adelanto) {
        datos.adelanto = true;
        datos.persona = e.persona;
        datos.categoriaId = CATEGORIA_ADELANTO;
        datos.presupuestoId = deleteField();
      } else if (t.adelanto || t.persona) {
        // Lo habían convertido en adelanto a mano: vuelve a ser gasto real
        datos.adelanto = deleteField();
        datos.persona = deleteField();
        datos.saldado = deleteField();
        if (t.categoriaId === CATEGORIA_ADELANTO) datos.categoriaId = destino.categoriaId;
      }
      ops.push(b => b.update(doc(db, 'transacciones', t.id), datos));
    });

    cambios.borrar.forEach(t => ops.push(b => b.delete(doc(db, 'transacciones', t.id))));

    for (let i = 0; i < ops.length; i += 450) {
      const batch = writeBatch(db);
      ops.slice(i, i + 450).forEach(op => op(batch));
      await confirmarLote(batch);
    }
  };

  // ── PRESUPUESTOS ─────────────────────────────────────────────────────────
  const crearPresupuesto = async (presupuesto: Omit<Presupuesto, 'id' | 'usuarioId' | 'createdAt'>) => {
    if (!user) throw new Error('Usuario no autenticado');
    comprobarLimite('presupuestos', presupuestos.length, esPlus);
    // Todo presupuesto nace con un grupoId estable. Si quien llama no lo aporta
    // (presupuesto nuevo e independiente), se genera uno propio.
    const grupoId = presupuesto.grupoId ?? crypto.randomUUID();
    // Firestore no acepta undefined — filtramos antes de escribir
    const datos = Object.fromEntries(
      Object.entries({ ...presupuesto, grupoId, usuarioId: user.uid, createdAt: Timestamp.now() })
        .filter(([, v]) => v !== undefined)
    );
    await setDoc(doc(collection(db, 'presupuestos')), datos);
  };

  const actualizarPresupuesto = async (id: string, presupuesto: Partial<Presupuesto>) => {
    const datos: Record<string, any> = Object.fromEntries(Object.entries(presupuesto).filter(([, v]) => v !== undefined));
    // Los importes por mes solo existen en los mensuales repetitivos: si deja de
    // serlo, se quitan para que no queden datos sueltos que nadie lee
    if (presupuesto.periodicidad === 'anual' || presupuesto.repetitivo === false) {
      datos.limitesMes = deleteField();
      datos.historialLimite = deleteField();
    }
    await updateDoc(doc(db, 'presupuestos', id), datos);
  };

  const eliminarPresupuesto = async (id: string) => {
    await deleteDoc(doc(db, 'presupuestos', id));
  };

  const migrarPresupuestoAAnual = async (params: {
    presupuestoMensualId: string;
    anioInicio: number;
    mesInicio: number;
  }): Promise<string> => {
    if (!user) throw new Error('Usuario no autenticado');

    const original = presupuestos.find(p => p.id === params.presupuestoMensualId);
    if (!original) throw new Error('Presupuesto no encontrado');

    // Modelo evergreen: un único documento por presupuesto. Convertir de mensual a
    // anual es un simple update in-place del MISMO documento; su id no cambia, así
    // que las transacciones vinculadas (que apuntan a este id) siguen vinculadas sin
    // reasignación alguna. Ya no hay instancias mensuales "hermanas" que borrar.
    await updateDoc(doc(db, 'presupuestos', params.presupuestoMensualId), {
      periodicidad:        'anual',
      mes:                 String(params.anioInicio),
      limite:              original.limite * 12,
      mesInicio:           params.mesInicio,
      anioInicio:          params.anioInicio,
      esObjetivoAhorro:    original.esObjetivoAhorro ?? false,
      migradoDesdeMensual: true,
      // Un anual no tiene importes por mes: lo que hubiera de cuando era mensual sobra
      limitesMes:          deleteField(),
      historialLimite:     deleteField(),
    });

    return params.presupuestoMensualId;
  };

  // ── TRASPASOS ────────────────────────────────────────────────────────────
  const crearTraspaso = async (traspaso: Omit<Traspaso, 'id' | 'usuarioId' | 'createdAt'>) => {
    if (!user) throw new Error('Usuario no autenticado');

    const baseSeq = nextSeq();
    const traspasoRef = doc(collection(db, 'traspasos'));
    const traspasoId = traspasoRef.id;
    const fechaTs = Timestamp.fromDate(new Date(traspaso.fecha));
    const desc = traspaso.descripcion ? `Traspaso: ${traspaso.descripcion}` : 'Traspaso entre cuentas';

    // 1. Registro maestro
    await setDoc(traspasoRef, {
      ...traspaso,
      usuarioId: user.uid,
      createdAt: Timestamp.now(),
      secuencia: baseSeq,
      fecha: fechaTs,
    });

    // 2. Salida (gasto) en cuenta origen
    // Si el destino es una hucha, se etiqueta con la categoría/presupuesto indicados
    const salidaData: any = {
      tipo: 'gasto',
      cantidad: traspaso.cantidad,
      categoriaId: traspaso.categoriaOrigenId || '__traspaso__',
      cuentaId: traspaso.cuentaOrigenId,
      descripcion: desc,
      fecha: fechaTs,
      usuarioId: user.uid,
      createdAt: Timestamp.now(),
      secuencia: baseSeq + 1,
      traspasoId,
    };
    if (traspaso.presupuestoOrigenId) salidaData.presupuestoId = traspaso.presupuestoOrigenId;
    await setDoc(doc(collection(db, 'transacciones')), salidaData);

    // 3. Entrada (ingreso) en cuenta destino — para trazabilidad y cálculo de saldo
    await setDoc(doc(collection(db, 'transacciones')), {
      tipo: 'ingreso',
      cantidad: traspaso.cantidad,
      categoriaId: '__traspaso__',
      cuentaId: traspaso.cuentaDestinoId,
      descripcion: desc,
      fecha: fechaTs,
      usuarioId: user.uid,
      createdAt: Timestamp.now(),
      secuencia: baseSeq + 2,
      traspasoId,
    });
    // El saldo se recalcula automáticamente vía useMemo al añadirse las transacciones
  };

  const eliminarTraspaso = async (id: string) => {
    const vinculadas = transacciones.filter(t => t.traspasoId === id);
    for (const t of vinculadas) await deleteDoc(doc(db, 'transacciones', t.id));
    // Si era un traspaso a inversión, eliminar también la aportación vinculada
    const aportacion = aportacionesInversion.find(a => a.traspasoRefId === id);
    if (aportacion) await deleteDoc(doc(db, 'aportacionesInversion', aportacion.id));
    await deleteDoc(doc(db, 'traspasos', id));
  };

  // ── TRASPASO A INVERSIÓN ─────────────────────────────────────────────────
  const crearTraspasoAInversion = async (params: {
    cuentaOrigenId: string;
    inversionDestinoId: string;
    cantidad: number;
    descripcion?: string;
    fecha: Date;
    presupuestoDestinoId?: string;
  }) => {
    if (!user) throw new Error('Usuario no autenticado');
    const inversion = inversiones.find(i => i.id === params.inversionDestinoId);
    if (!inversion) throw new Error('Inversión no encontrada');

    const { porcentaje, comisionAplicada, cantidadInvertida } = calcularComisionAportacion(
      inversion.comision,
      new Date(inversion.fechaInicio),
      params.fecha,
      params.cantidad,
    );

    const baseSeq = nextSeq();
    const traspasoRef = doc(collection(db, 'traspasos'));
    const traspasoId  = traspasoRef.id;
    const fechaTs     = Timestamp.fromDate(new Date(params.fecha));
    const desc        = params.descripcion || `Aportación: ${inversion.nombre}`;

    // 1. Traspaso maestro (para trazabilidad y visualización en Transacciones)
    await setDoc(traspasoRef, {
      cuentaOrigenId:      params.cuentaOrigenId,
      inversionDestinoId:  params.inversionDestinoId,
      cantidad:            params.cantidad,
      descripcion:         desc,
      fecha:               fechaTs,
      usuarioId:           user.uid,
      createdAt:           Timestamp.now(),
      secuencia:           baseSeq,
      ...(params.presupuestoDestinoId ? { presupuestoDestinoId: params.presupuestoDestinoId } : {}),
    });

    // 2. Gasto en cuenta origen (reduce el saldo)
    await setDoc(doc(collection(db, 'transacciones')), {
      tipo:        'gasto',
      cantidad:    params.cantidad,
      categoriaId: '__inversion__',
      cuentaId:    params.cuentaOrigenId,
      descripcion: desc,
      fecha:       fechaTs,
      usuarioId:   user.uid,
      createdAt:   Timestamp.now(),
      secuencia:   baseSeq + 1,
      traspasoId,
    });

    // 3. Aportación registrada en el producto de inversión
    const aportData: Record<string, unknown> = {
      inversionId:        params.inversionDestinoId,
      fecha:              fechaTs,
      cantidadAportada:   params.cantidad,
      porcentajeComision: porcentaje,
      comisionAplicada,
      cantidadInvertida,
      cuentaOrigenId:     params.cuentaOrigenId,
      traspasoRefId:      traspasoId,
      usuarioId:          user.uid,
      createdAt:          Timestamp.now(),
    };
    if (desc) aportData.descripcion = desc;
    await setDoc(doc(collection(db, 'aportacionesInversion')), aportData);
  };

  // ── RETIRO DESDE INVERSIÓN ───────────────────────────────────────────────
  const crearRetiroDesdeInversion = async (params: {
    inversionId: string;
    cuentaDestinoId: string;
    cantidad: number;
    descripcion?: string;
    fecha: Date;
  }) => {
    if (!user) throw new Error('Usuario no autenticado');
    const inversion = inversiones.find(i => i.id === params.inversionId);
    if (!inversion) throw new Error('Inversión no encontrada');

    const baseSeq = nextSeq();
    const traspasoRef = doc(collection(db, 'traspasos'));
    const traspasoId  = traspasoRef.id;
    const fechaTs     = Timestamp.fromDate(new Date(params.fecha));
    const desc        = params.descripcion || `Retiro: ${inversion.nombre}`;

    // 1. Traspaso maestro (para trazabilidad y visualización en Transacciones)
    await setDoc(traspasoRef, {
      cuentaDestinoId:    params.cuentaDestinoId,
      inversionOrigenId:  params.inversionId,
      cantidad:           params.cantidad,
      descripcion:        desc,
      fecha:              fechaTs,
      usuarioId:          user.uid,
      createdAt:          Timestamp.now(),
      secuencia:          baseSeq,
    });

    // 2. Ingreso en cuenta destino (aumenta el saldo)
    await setDoc(doc(collection(db, 'transacciones')), {
      tipo:        'ingreso',
      cantidad:    params.cantidad,
      categoriaId: '__inversion__',
      cuentaId:    params.cuentaDestinoId,
      descripcion: desc,
      fecha:       fechaTs,
      usuarioId:   user.uid,
      createdAt:   Timestamp.now(),
      secuencia:   baseSeq + 1,
      traspasoId,
    });

    // 3. Actualizar valorActual de la inversión (resta el retiro)
    const nuevoValor = Math.max(0, inversion.valorActual - params.cantidad);
    await updateDoc(doc(db, 'inversiones', params.inversionId), {
      valorActual: nuevoValor
    });

    // 4. Retiro registrado en el producto de inversión
    const retiroData: Record<string, unknown> = {
      inversionId:       params.inversionId,
      fecha:             fechaTs,
      cantidadRetirada:  params.cantidad,
      cuentaDestinoId:   params.cuentaDestinoId,
      traspasoRefId:     traspasoId,
      usuarioId:         user.uid,
      createdAt:         Timestamp.now(),
    };
    if (desc) retiroData.descripcion = desc;
    await setDoc(doc(collection(db, 'retirosInversion')), retiroData);
  };

  const eliminarRetiroInversion = async (id: string) => {
    const retiro = retirosInversion.find(r => r.id === id);
    if (!retiro) return;

    // Eliminar transacción vinculada (ingreso en cuenta destino)
    const vinculada = transacciones.find(t => t.traspasoId === retiro.traspasoRefId && t.tipo === 'ingreso');
    if (vinculada) await deleteDoc(doc(db, 'transacciones', vinculada.id));

    // Eliminar traspaso
    if (retiro.traspasoRefId) await deleteDoc(doc(db, 'traspasos', retiro.traspasoRefId));

    // Eliminar retiro
    await deleteDoc(doc(db, 'retirosInversion', id));
  };

  // ── TRASPASOS RECURRENTES ────────────────────────────────────────────────
  const crearTraspasoRecurrente = async (tr: Omit<TraspasoRecurrente, 'id' | 'usuarioId' | 'createdAt'>) => {
    if (!user) throw new Error('Usuario no autenticado');
    comprobarLimite('periodicos', traspasosRecurrentes.length + transaccionesRecurrentes.length, esPlus);
    await setDoc(doc(collection(db, 'traspasosRecurrentes')), {
      ...tr,
      usuarioId: user.uid,
      createdAt: Timestamp.now(),
      proximaFecha: Timestamp.fromDate(new Date(tr.proximaFecha)),
    });
  };

  const ejecutarTraspasoRecurrente = async (id: string) => {
    const tr = traspasosRecurrentes.find(t => t.id === id);
    if (!tr) return;

    if (tr.inversionDestinoId) {
      // Destino es un producto de inversión
      await crearTraspasoAInversion({
        cuentaOrigenId:     tr.cuentaOrigenId,
        inversionDestinoId: tr.inversionDestinoId,
        cantidad:           tr.cantidad,
        descripcion:        tr.descripcion || tr.nombre,
        fecha:              new Date(),
        ...(tr.presupuestoDestinoId ? { presupuestoDestinoId: tr.presupuestoDestinoId } : {}),
      });
    } else {
      await crearTraspaso({
        cuentaOrigenId:  tr.cuentaOrigenId,
        cuentaDestinoId: tr.cuentaDestinoId!,
        cantidad:        tr.cantidad,
        descripcion:     tr.descripcion || tr.nombre,
        fecha:           new Date(),
        ...(tr.categoriaOrigenId  ? { categoriaOrigenId:  tr.categoriaOrigenId  } : {}),
        ...(tr.presupuestoOrigenId ? { presupuestoOrigenId: tr.presupuestoOrigenId } : {}),
      });
    }

    // Avanzar próxima fecha al siguiente ciclo
    const proxima = proximaTrasEjecutar(tr.proximaFecha, tr.periodicidad as any);

    // Desactivar si la próxima ejecución supera la fecha fin
    const finSuperado = tr.fechaFin && proxima > new Date(tr.fechaFin);
    await updateDoc(doc(db, 'traspasosRecurrentes', id), {
      proximaFecha: Timestamp.fromDate(proxima),
      ...(finSuperado ? { activo: false } : {}),
    });
  };

  const actualizarTraspasoRecurrente = async (id: string, tr: Partial<TraspasoRecurrente>) => {
    const datos: any = { ...tr };
    if (tr.proximaFecha) datos.proximaFecha = Timestamp.fromDate(new Date(tr.proximaFecha));
    const limpio: any = Object.fromEntries(Object.entries(datos).filter(([, v]) => v !== undefined));
    await updateDoc(doc(db, 'traspasosRecurrentes', id), limpio);
  };

  const eliminarTraspasoRecurrente = async (id: string) => {
    await deleteDoc(doc(db, 'traspasosRecurrentes', id));
  };

  // ── TRANSACCIONES RECURRENTES ────────────────────────────────────────────
  const crearTransaccionRecurrente = async (tr: Omit<TransaccionRecurrente, 'id' | 'usuarioId' | 'createdAt'>) => {
    if (!user) throw new Error('Usuario no autenticado');
    comprobarLimite('periodicos', traspasosRecurrentes.length + transaccionesRecurrentes.length, esPlus);
    await setDoc(doc(collection(db, 'transaccionesRecurrentes')), {
      ...tr,
      usuarioId: user.uid,
      createdAt: Timestamp.now(),
      proximaFecha: Timestamp.fromDate(new Date(tr.proximaFecha)),
    });
  };

  const ejecutarTransaccionRecurrente = async (id: string) => {
    const tr = transaccionesRecurrentes.find(t => t.id === id);
    if (!tr) return;

    // Resolver el presupuesto activo para el período actual.
    // La plantilla puede tener presupuestoId de un mes anterior; buscamos el presupuesto
    // con mismo nombre + categoría + periodicidad cuyo mes coincide con el período actual.
    let presupuestoIdResuelto: string | undefined;
    if (tr.presupuestoId) {
      const plantillaPresupuesto = presupuestos.find(p => p.id === tr.presupuestoId);
      if (plantillaPresupuesto) {
        const hoy = new Date();
        const periodoActual = plantillaPresupuesto.periodicidad === 'mensual'
          ? `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`
          : String(hoy.getFullYear());
        // Buscar presupuesto activo del período actual con mismo nombre + categoría
        const presupuestoActual = presupuestos.find(p =>
          p.nombre === plantillaPresupuesto.nombre &&
          p.categoriaId === plantillaPresupuesto.categoriaId &&
          p.periodicidad === plantillaPresupuesto.periodicidad &&
          p.mes === periodoActual
        );
        presupuestoIdResuelto = presupuestoActual?.id ?? tr.presupuestoId;
      } else {
        presupuestoIdResuelto = tr.presupuestoId;
      }
    }

    await crearTransaccion({
      tipo: tr.tipo,
      cantidad: tr.cantidad,
      categoriaId: tr.categoriaId,
      cuentaId: tr.cuentaId,
      descripcion: tr.descripcion || tr.nombre,
      fecha: new Date(),
      recurrenteId: tr.id,
      ...(presupuestoIdResuelto ? { presupuestoId: presupuestoIdResuelto } : {}),
    });

    // Avanzar al menos un periodo, también si se ejecuta antes de la fecha prevista
    const proxima = proximaTrasEjecutar(tr.proximaFecha, tr.periodicidad);

    // Desactivar si la próxima ejecución supera la fecha fin
    const finSuperado = tr.fechaFin && proxima > new Date(tr.fechaFin);
    await updateDoc(doc(db, 'transaccionesRecurrentes', id), {
      proximaFecha: Timestamp.fromDate(proxima),
      ...(finSuperado ? { activo: false } : {}),
    });
  };

  const actualizarTransaccionRecurrente = async (id: string, tr: Partial<TransaccionRecurrente>) => {
    const datos: any = { ...tr };
    if (tr.proximaFecha) datos.proximaFecha = Timestamp.fromDate(new Date(tr.proximaFecha));
    const limpio: any = Object.fromEntries(Object.entries(datos).filter(([, v]) => v !== undefined));
    await updateDoc(doc(db, 'transaccionesRecurrentes', id), limpio);
  };

  const eliminarTransaccionRecurrente = async (id: string) => {
    await deleteDoc(doc(db, 'transaccionesRecurrentes', id));
  };

  // ── INVERSIONES ──────────────────────────────────────────────────────────
  const crearInversion = async (inv: Omit<Inversion, 'id' | 'usuarioId' | 'createdAt'>) => {
    if (!user) throw new Error('Usuario no autenticado');
    if (inv.activo !== false) comprobarLimite('inversiones', inversionesActivas(inversiones), esPlus);
    const datos: Record<string, unknown> = {
      ...inv,
      fechaInicio: Timestamp.fromDate(new Date(inv.fechaInicio)),
      usuarioId: user.uid,
      createdAt: Timestamp.now(),
    };
    // Limpiar undefined
    Object.keys(datos).forEach(k => datos[k] === undefined && delete datos[k]);
    await setDoc(doc(collection(db, 'inversiones')), datos);
  };

  const actualizarInversion = async (id: string, inv: Partial<Inversion>) => {
    const datos: Record<string, any> = { ...inv };
    if (inv.fechaInicio) datos.fechaInicio = Timestamp.fromDate(new Date(inv.fechaInicio));
    const limpio: Record<string, any> = Object.fromEntries(Object.entries(datos).filter(([, v]) => v !== undefined));
    await updateDoc(doc(db, 'inversiones', id), limpio);
  };

  const eliminarInversion = async (id: string) => {
    await deleteDoc(doc(db, 'inversiones', id));
  };

  const actualizarValorInversion = async (id: string, valor: number, fecha: Date) => {
    if (!user) throw new Error('Usuario no autenticado');
    await updateDoc(doc(db, 'inversiones', id), { valorActual: valor });
    await setDoc(doc(collection(db, 'valoracionesInversion')), {
      inversionId: id,
      fecha:       Timestamp.fromDate(fecha),
      valorTotal:  valor,
      usuarioId:   user.uid,
      createdAt:   Timestamp.now(),
    });
  };

  const eliminarAportacionInversion = async (id: string) => {
    await deleteDoc(doc(db, 'aportacionesInversion', id));
  };

  // ── Value ────────────────────────────────────────────────────────────────
  const value: DataContextType = {
    cuentas,   // saldo calculado
    categorias,
    transacciones,
    presupuestos,
    traspasos,
    traspasosRecurrentes,
    inversiones,
    aportacionesInversion,
    retirosInversion,
    valoracionesInversion,
    loading,
    crearCuenta, actualizarCuenta, eliminarCuenta,
    categoriasCargadas, cuentasCargadas, transaccionesCargadas, crearCategoria, crearCategorias, actualizarCategoria, eliminarCategoria,
    crearTransaccion, actualizarTransaccion, eliminarTransaccion, saldarAdelantosDe, aplicarSincronizacionPlan,
    crearPresupuesto, actualizarPresupuesto, eliminarPresupuesto, migrarPresupuestoAAnual,
    crearTraspaso, eliminarTraspaso,
    crearTraspasoRecurrente, actualizarTraspasoRecurrente, ejecutarTraspasoRecurrente, eliminarTraspasoRecurrente,
    transaccionesRecurrentes,
    crearTransaccionRecurrente, actualizarTransaccionRecurrente, ejecutarTransaccionRecurrente, eliminarTransaccionRecurrente,
    crearInversion, actualizarInversion, eliminarInversion,
    actualizarValorInversion, eliminarAportacionInversion,
    crearTraspasoAInversion,
    crearRetiroDesdeInversion, eliminarRetiroInversion,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) throw new Error('useData debe ser usado dentro de DataProvider');
  return context;
};
